
// Packages
import { defineComponent, PropType } from 'vue';
import { mapState, mapActions } from 'pinia';
import { PortalTarget } from 'portal-vue';
import { datadogRum } from '@datadog/browser-rum';

// Helpers
import { getAppVariable } from '@white-label-helper/get-app-variable';
import { getAppPaymentProvider } from '@white-label-helper/get-app-payment-provider';
import { getProductTypeCustomerGroupLabels } from '@white-label-helper/get-product-type-customer-group-labels';
import {
  goToApp,
  getDomainUrl,
  ACCOUNT_URLS,
  DOMAINS_NAMES,
} from '@white-label-helper/switch-app';
import { checkIsLessOneHour } from '@white-label-helper/time-helpers';
import { isBookingPortalFlow } from '@white-label-helper/booking-portal-flow';
import { addCustomMetric } from '@white-label-helper/shared-helpers';
import { isSycurioFlow } from '@white-label-helper/is-sycurio-flow';
import {
  required,
  alphaNumSpace,
  notNumber,
  requiredIf,
  email,
  maxLength,
} from '@white-label-helper/vuelidate';
import {
  guessTimezone,
  format,
  utcToZonedTime,
  DATE_TIME_FORMATS,
} from '@white-label-helper/date-utilities';
import { smoothScroll } from '@white-label-helper/scroll';
import { getCookieDomain } from '@white-label-helper/get-cookie-domain';

// Constants
import {
  PAYMENT_PROVIDERS,
  CANT_FIND_FLIGHT_ID,
  PRODUCT_TYPES,
  FEATURE_FLAGS,
  VEHICLE_OCCUPANTS,
} from '@white-label-configuration/constants';

// Mixins
import checkoutForm from '@white-label-helper/mixin-checkout-form';

// Stores
import { useMarketingPreferencesStore } from '@white-label-store/marketing-preferences';
import { useLoungePassStore } from '@white-label-store/lounge-pass';
import { useTravelInsuranceStore } from '@white-label-store/travel-insurance';
import { useUserInfoStore } from '@white-label-store/user-info';
import { usePreloaderControllerStore } from '@white-label-store/preloader-controller';

// Types
import type { LocaleMessages } from 'vue-i18n';
import type { Partners } from '@white-label-types/partners';
import type { SerializeOptions as CookieSerializeOptions } from 'cookie';
import type {
  BraintreeConfig,
  StripeConfig,
  CartItem,
  WindcaveConfig,
  AdvamConfig,
} from '@white-label-types/parking-checkout';
import type { UserData } from '@white-label-types/stores';
import type { Terminal } from '@white-label-types/search-box';

// Components
import IconMessage from '../icon-message/icon-message.vue';
import ControlButton from '../control-button/control-button.vue';
import TextField from '../text-field/text-field.vue';
import PaymentPolicies from '../payment-policies/payment-policies.vue';
import PaymentGatewayBraintree from '../payment-gateway-braintree/payment-gateway-braintree.vue';
import PaymentGatewayStripe from '../payment-gateway-stripe/payment-gateway-stripe.vue';
import PaymentGatewayWindcave from '../payment-gateway-windcave/payment-gateway-windcave.vue';
import PaymentGatewayAdvam from '../payment-gateway-advam/payment-gateway-advam.vue';
import PaymentGatewaySycurio from '../payment-gateway-sycurio/payment-gateway-sycurio.vue';
import PaymentMarketingChannels from '../payment-marketing-channels/payment-marketing-channels.vue';
import PaymentPageAdditionalTravellers from '../payment-page-additional-travellers/payment-page-additional-travellers.vue';
import Checkbox from '../checkbox/checkbox.vue';
import PhoneField from '../phone-field/phone-field.vue';
import FlightLookup from '../flight-lookup/flight-lookup.vue';
import Tooltip from '../tooltip/tooltip.vue';
import LookupConfirmation from '../lookup-confirmation/lookup-confirmation.vue';
import DropdownWrapper from '../dropdown-wrapper/dropdown-wrapper.vue';
import IconS3 from '@white-label-icon/icons-s3';

export default defineComponent({
  name: 'PaymentFormManageBooking',

  components: {
    ControlButton,
    Policies: PaymentPolicies,
    Checkbox: Checkbox,
    PaymentGatewaysSharedBraintree: PaymentGatewayBraintree,
    PaymentGatewaysSharedStripe: PaymentGatewayStripe,
    PaymentGatewaysSharedWindcave: PaymentGatewayWindcave,
    PaymentGatewaysSharedAdvam: PaymentGatewayAdvam,
    PaymentGatewaysSharedSycurio: PaymentGatewaySycurio,
    PaymentPageAdditionalTravellers,
    IconS3,
    IconLockStar: () => import('@white-label-icon/icon-lock-star'),
    IconMessage,
    TextField,
    PhoneField,
    MarketingChannels: PaymentMarketingChannels,
    PortalTarget,
    FlightLookup,
    Tooltip,
    LookupConfirmation,
    DropdownWrapper,
  },

  mixins: [checkoutForm],

  props: {
    paymentsConfig: {
      type: Object as PropType<
        BraintreeConfig | StripeConfig | WindcaveConfig | AdvamConfig
      >,
      required: true,
    },

    upgradingOriginalId: {
      type: Boolean,
      default: false,
    },

    paymentsConfigReceived: {
      type: Boolean,
      required: true,
      default: false,
    },

    paymentIntentUpdated: {
      type: Boolean,
      default: false,
    },

    title: {
      type: String,
      default: '',
    },

    isPriceEqualToOriginalBooking: {
      type: Boolean,
      default: false,
    },

    paymentNeeded: {
      type: Boolean,
      default: false,
    },

    orderPrice: {
      type: Number,
      default: 0,
    },

    userData: {
      type: Object as PropType<{ [key: string]: any }>,
      required: true,
    },

    amountToPay: {
      type: Number,
      required: true,
    },

    cancellationPolicies: {
      type: Array as PropType<
        CartItem['inventory_option']['cancellation_policies']
      >,
      default: () => [],
    },

    amendmentPolicies: {
      type: Array as PropType<
        CartItem['inventory_option']['amendment_policies']
      >,
      default: () => [],
    },

    entryDateTime: {
      type: String,
      default: '',
    },

    isLoungesProduct: {
      type: Boolean,
      default: false,
    },
    searchCriteria: {
      type: Object,
    },

    extendFlow: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  validations() {
    // null is allowed here to support the "can't-find-flight" option.
    // Empty string should still fail validation.
    const conditionallyRequired = (fieldName) => {
      return function (value) {
        const required = this.checkoutForm_checkIfFieldRequired(fieldName);
        if (!required) return true;
        if (value === null) return true;
        return typeof value === 'string' && value.trim() !== '';
      };
    };

    const userDetailsValidations = {
      first_name: {
        required,
        notNumber,
      },
      last_name: {
        required,
        notNumber,
      },
      license_plate: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('plate_number');
        }),
        alphaNumSpace(val: string) {
          // Fix when an empty field is considered an error
          // We have a separate validation to check if this field is required
          if (val) {
            return alphaNumSpace(val);
          }
          return true;
        },
      },
      phone: {
        phone_number: {
          required: requiredIf(function checkIfRequired() {
            // @ts-ignore - `this` is not recognized in its context
            return (
              this.isFieldVisibleInExtendFlow(this.userDetails.phone?.phone_number) && (
                this.checkoutForm_checkIfFieldRequired('phone_number') ||
                // @ts-ignore - `this` is not recognized in its context
                ( this.userDetails.marketing_channels?.sms && this.checkoutForm_checkFieldVisibility('phone_number') )
              )
            );
          }),
          isValid(): boolean {
            // @ts-ignore - `this` is not recognized in its context
            const isEmpty = !this.userDetails.phone?.phone_number?.length;
            // @ts-ignore - `this` is not recognized in its context
            return !isEmpty ? this.phoneValid : true;
          },
        },
      },
      zipcode: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.isFieldVisibleInExtendFlow(this.userDetails.zipcode) && this.checkoutForm_checkIfFieldRequired('zipcode');
        }),
      },
      outbound_flight: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.isFieldVisibleInExtendFlow(this.userDetails.outbound_flight) && this.checkoutForm_checkIfFieldRequired(
            'outbound_flight_number'
          ) && !this.displayFlightLookup;
        }),
      },
      inbound_flight: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.isFieldVisibleInExtendFlow(this.userDetails.inbound_flight) && this.checkoutForm_checkIfFieldRequired('inbound_flight_number');
        }),
        conditionallyValid: conditionallyRequired('inbound_flight_number'),
      },
      number_of_people_in_vehicle: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired(
            'number_of_people_in_vehicle',
          );
        }),
      },
      ...(this.isBookingPortalFlowEnabled
        ? {
            others_email: {
              email,
            },
            booker_email: {
              required: requiredIf(() => this.isBookingPortalFlowEnabled),
              email,
            },
          }
        : {}),
      vehicle_make: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('vehicle_make');
        }),
        maxLength: maxLength(this.vehicleFieldsMaxLength),
      },
      vehicle_model: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('vehicle_model');
        }),
        maxLength: maxLength(this.vehicleFieldsMaxLength),
      },
      vehicle_colour: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('vehicle_colour');
        }),
        maxLength: maxLength(this.vehicleFieldsMaxLength),
      },
      cruise_ship_name: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('cruise_ship_name');
        }),
        maxLength: maxLength(this.cruiseShipNameMaxLength),
      },
      terminal_name: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore - `this` is not recognized in its context
          return this.checkoutForm_checkIfFieldRequired('terminal_name');
        }),
      },
    };

    const travellerNamesValidation = {
      required,
      notNumber,
    };

    const validations = {
      userDetails: userDetailsValidations,
    };

    if (this.displayLoungePassAdditionalTravellers) {
      validations.loungePassAdditionalTravellersDetails = {
        $each: {
          firstName: {
            required: requiredIf(function checkIfRequired(traveller) {
              return (
                this.isLoungePassAdditionalTravellersDetailsRequired ||
                traveller.lastName.length
              );
            }),
            notNumber,
          },
          lastName: {
            required: requiredIf(function checkIfRequired(traveller) {
              return (
                this.isLoungePassAdditionalTravellersDetailsRequired ||
                traveller.firstName.length
              );
            }),
            notNumber,
          },
        },
      };
    }

    validations.vehicleLookUp = {
      vehicleMaxWeightConfirmed: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore
          return this.checkoutForm_checkIfFieldRequired('max_vehicle_weight');
        }),
        isConfirmed(value: boolean) {
          if (!this.checkoutForm_checkIfFieldRequired('max_vehicle_weight'))
            return true;
          return value === true;
        },
      },
      vehicleRoadworthyConfirmed: {
        required: requiredIf(function checkIfRequired() {
          // @ts-ignore
          return this.checkoutForm_checkIfFieldRequired('vehicle_roadworthy');
        }),
        isConfirmed(value: boolean) {
          if (!this.checkoutForm_checkIfFieldRequired('vehicle_roadworthy'))
            return true;
          return value === true;
        },
      },
    };

    return validations;
  },

  data() {
    const defaultData = {
      showMarketingPreferences: false,
      buttonLoading: false,
      userDetails: {
        first_name: '',
        last_name: '',
        email: '',
        phone: {
          phone_number: '',
          formatted_number: '',
          country_code: '',
        },
        license_plate: '',

        zipcode: '',
        outbound_flight: '',
        outbound_airline: '',
        outbound_airport: '',
        outbound_terminal: '',
        inbound_flight: '',
        inbound_airline: '',
        inbound_airport: '',
        inbound_terminal: '',
        number_of_people_in_vehicle: null,
        send_booker_email: false,
        send_customer_email: false,
        send_others_email: false,
        marketing_channels: {},
        vehicle_make: '',
        vehicle_model: '',
        vehicle_colour: '',
        cruise_ship_name: '',
        terminal_name: '',
      } as UserData,
      phoneValid: false,
      others_email: '',
      booker_email: '',
      showDropdownExpanded: false,
      loungePassAdditionalTravellersDetails: [],
      numberInVehicleOpened: false,
      terminalNameOpened: false,
      vehicleLookUp: {
        error: false,
        loading: false,
        success: false,
        enterManually: false,
        showDetails: false,
        vehicleMaxWeightConfirmed: false,
        vehicleRoadworthyConfirmed: false,
        grossVehicleWeight: null,
        vehicleRoadworthyValid: false,
        vehicleRoadworthinessAndWeightError: '',
      },
    };

    return defaultData;
  },

  computed: {
    ...mapState(useUserInfoStore, {
      outboundFlightLookupItems: 'readOutboundFlightLookupItems',
      inboundFlightLookupItems: 'readInboundFlightLookupItems',
      outboundFlightSelected: 'readOutboundFlightLookupSelected',
      inboundFlightSelected: 'readInboundFlightLookupSelected',
      findOutboundFlightById: 'findOutboundFlightById',
      findInboundFlightById: 'findInboundFlightById',
      flightLookupLoading: 'readFlightLookupLoading',
    }),
    ...mapState(useMarketingPreferencesStore, {
      readUserMarketingSubscription: 'readUserMarketingSubscription',
      readMarketingPreferences: 'readMarketingPreferences',
    }),
    ...mapState(useTravelInsuranceStore, {
      travelInsuranceCartItem: 'travelInsuranceCartItem',
    }),
    ...mapState(useLoungePassStore, {
      loungePassItem: 'loungePassItem',
      displayLoungePassAdditionalTravellers:
        'displayLoungePassAdditionalTravellers',
      isLoungePassAdditionalTravellersDetailsRequired:
        'isLoungePassAdditionalTravellersDetailsRequired',
    }),
    displayOutboundFlightLookup() {
      const isVisible = this.checkoutForm_checkFieldVisibility(
        'outbound_flight_number',
      );

      return isVisible && this.flightWasSelected && this.flightNumber;
    },

    displayInboundFlightLookup() {
      return this.checkoutForm_checkFieldVisibility('inbound_flight_number');
    },

    displayBraintreeGateway(): boolean {
      return (
        getAppPaymentProvider() === PAYMENT_PROVIDERS.BRAINTREE &&
        this.paymentsConfigReceived
      );
    },

    displayStripeGateway(): boolean {
      return (
        getAppPaymentProvider() === PAYMENT_PROVIDERS.STRIPE &&
        this.paymentsConfigReceived
      );
    },

    displayWindcaveGateway(): boolean {
      return (
        getAppPaymentProvider() === PAYMENT_PROVIDERS.WINDCAVE &&
        this.paymentsConfigReceived
      );
    },

    displayAdvamGateway(): boolean {
      return (
        getAppPaymentProvider() === PAYMENT_PROVIDERS.ADVAM &&
        this.paymentsConfigReceived
      );
    },

    formLocked(): boolean {
      return !!this.upgradingOriginalId;
    },

    payButtonText(): LocaleMessages | string {
      const baseTranslationString = 'UI.button';
      let translationString = 'payForBooking';

      // @ts-ignore - Mixin method
      if (this.checkoutForm_paymentMethod === 'paypal')
        translationString = 'paypal';

      if (!this.paymentNeeded || this.isBookingPortalFlowEnabled)
        translationString = 'updateBooking';

      return this.$t(`${baseTranslationString}.${translationString}`);
    },

    payButtonId(): string {
      let btnId = '';

      // @ts-ignore - Mixin method
      if (this.checkoutForm_paymentMethod === 'card')
        btnId = 'GAPaymentSubmitCard';
      // @ts-ignore - Mixin method
      if (this.checkoutForm_paymentMethod === 'paypal')
        btnId = 'GAPaymentSubmitPaypal';

      if (!this.paymentNeeded) btnId = 'GAUpdateBooking';

      return this.isPriceEqualToOriginalBooking ? '' : btnId;
    },

    partnerTimeZone(): Partners['poi']['timezone'] {
      return getAppVariable('poi.timezone') || guessTimezone();
    },

    userMarketingSubscription() {
      return this.readUserMarketingSubscription;
    },

    partnerMarketingPreferences() {
      return this.readMarketingPreferences;
    },
    vehicleFieldsMaxLength(): number {
      return 25;
    },
    displayFlightLookup(): boolean {
      return (
        getAppVariable('is_flight_lookup_enabled') &&
        (this.checkoutForm_checkFieldVisibility('inbound_flight_number') ||
          this.checkoutForm_checkFieldVisibility('outbound_flight_number'))
      );
    },
    isBookingPortalFlowEnabled(): boolean {
      return isBookingPortalFlow();
    },

    isVehicleFeatureFlagEnabled(): boolean {
      return (
        this.$launchDarkly.variation(
          FEATURE_FLAGS.PT_2971_VEHICLE_LOOKUP_IMPROVED_UI,
        ) === true
      );
    },

    isVehicleRoadworthinessAndWeightEnabled(): boolean {
      return (
        this.$launchDarkly.variation(
          FEATURE_FLAGS.ECOM_3797_VEHICLE_ROADWORTHY_AND_WEIGHT,
        ) === true
      );
    },

    isVehicleRoadworthinessAndWeightValidationEnabled() {
      return (
        this.$launchDarkly.variation(
          FEATURE_FLAGS.ECOM_3837_VEHICLE_ROADWORTHY_AND_WEIGHT_VALIDATION,
        ) === true
      );
    },

    isLinkToNotAllowedVehiclesDisplayed(): boolean {
      return (
        this.$launchDarkly.variation(
          FEATURE_FLAGS.ECOM_3834_LINK_TO_LIST_OF_NOT_ALLOWED_VEHICLES,
        ) === true
      );
    },

    isVehicleRoadworthinessAndWeightCheckboxesDisabled(): boolean {
      if (
        this.displayVehicleLookupButton &&
        this.isVehicleRoadworthinessAndWeightValidationEnabled
      ) {
        return !this.vehicleLookUp.success;
      }
      return false;
    },

    vehicleMaxWeightValid() {
      return (
        this.vehicleLookUp.grossVehicleWeight !== null &&
        this.vehicleLookUp.grossVehicleWeight < 2500
      );
    },

    hideChangeVehicleInfo(): boolean {
      return (
        this.isVehicleRoadworthinessAndWeightValidationEnabled &&
        (this.checkoutForm_checkFieldVisibility('vehicle_roadworthy') ||
          this.checkoutForm_checkFieldVisibility('max_vehicle_weight'))
      );
    },

    displayVehicleDetails(): boolean {
      return (
        this.checkoutForm_checkFieldVisibility('plate_number') ||
        this.checkoutForm_checkFieldVisibility('number_of_people_in_vehicle') ||
        this.checkoutForm_checkFieldVisibility('vehicle_make') ||
        this.checkoutForm_checkFieldVisibility('vehicle_model') ||
        this.checkoutForm_checkFieldVisibility('vehicle_colour') ||
        this.checkoutForm_checkFieldVisibility('max_vehicle_weight') ||
        this.checkoutForm_checkFieldVisibility('vehicle_roadworthy')
      );
    },

    displayVehicleLookUp(): boolean {
      return (
        getAppVariable('is_vehicle_lookup_enabled') &&
        this.checkoutForm_checkFieldVisibility('plate_number') &&
        this.isVehicleFeatureFlagEnabled
      );
    },

    displayVehicleLookupButton(): boolean {
      return (
        this.displayVehicleLookUp &&
        (this.checkoutForm_checkFieldVisibility('vehicle_make') ||
          this.checkoutForm_checkFieldVisibility('vehicle_model') ||
          this.checkoutForm_checkFieldVisibility('vehicle_colour'))
      );
    },

    filteredLookupDetails() {
      const details = [
        this.checkoutForm_checkFieldVisibility('vehicle_make')
          ? this.userDetails.vehicle_make
          : null,
        this.checkoutForm_checkFieldVisibility('vehicle_model')
          ? this.userDetails.vehicle_model
          : null,
        this.checkoutForm_checkFieldVisibility('vehicle_colour')
          ? this.userDetails.vehicle_colour
          : null,
      ].filter(Boolean);
      return details.length > 0 ? details : null;
    },

    licensePlateSuccess() {
      if (this.displayVehicleLookupButton) {
        return this.vehicleLookUp.success;
      }
      return this.checkSuccess(
        this.$v.userDetails.license_plate,
        this.userDetails.license_plate,
        this.checkoutForm_checkIfFieldRequired('plate_number'),
      );
    },

    partnerId(): string {
      return getAppVariable('partner_id');
    },

    flightWasSelected(): boolean {
      return this.searchCriteria.is_flight_selected;
    },
    flightNumber(): string {
      return this.searchCriteria?.flight_number;
    },
    loungePassCustomerGroupLabels() {
      if (this.displayLoungePassAdditionalTravellers) {
        return getProductTypeCustomerGroupLabels(PRODUCT_TYPES.LOUNGES);
      }

      return {};
    },
    numberInVehicleOptions(): { id: number | string; name: string }[] {
      const options = [...VEHICLE_OCCUPANTS];
      return options.slice(1);
    },
    computedNumberInVehicle: {
      get(): { id: number | string; name: string } {
        return (
          this.numberInVehicleOptions.find(
            (item) =>
              item.name ===
              this.userDetails.number_of_people_in_vehicle?.toString(),
          ) || {
            name: '',
            id: '',
          }
        );
      },
      set(val: { id: number; name: string }): void {
        this.userDetails.number_of_people_in_vehicle = parseInt(val.name);
      },
    },
    numberInVehicleError(): boolean {
      // @ts-ignore
      return this.$v.userDetails.number_of_people_in_vehicle.$error;
    },
    displayTripDetails(): boolean {
      return (
        this.checkoutForm_checkFieldVisibility('terminal_name') ||
        this.checkoutForm_checkFieldVisibility('cruise_ship_name')
      );
    },
    terminalNameOptions(): Terminal[] {
      return getAppVariable<Terminal[]>('terminals', []);
    },
    computedTerminalName: {
      get(): Terminal {
        return (
          this.terminalNameOptions.find(
            (item) => item.name?.toString() === this.userDetails.terminal_name,
          ) || {
            name: '',
            id: '',
          }
        );
      },
      set(val: Terminal): void {
        this.userDetails.terminal_name = val.name?.toString() ?? '';
      },
    },
    terminalNameError(): boolean {
      // @ts-ignore
      return this.$v.userDetails.terminal_name.$error;
    },
    cruiseShipNameMaxLength(): number {
      return 50;
    },
  },

  created() {
    this.userDetails = {
      ...this.userDetails,
      ...this.userData,
      others_email: this.userData.booking_emails?.others_email || '',
      booker_email: this.userData.booking_emails?.booker_email || '',
      send_booker_email:
        this.userData.booking_emails?.send_booker_email || false,
      send_customer_email:
        this.userData.booking_emails?.send_customer_email || false,
      send_others_email:
        this.userData.booking_emails?.send_others_email || false,
      first_name: this.userData.user_first_name,
      last_name: this.userData.user_last_name,
      // @ts-ignore mixin method
      phone: this.checkoutForm_checkFieldVisibility('phone_number')
        ? { ...this.userData.phone }
        : { ...this.userDetails.phone },
      // @ts-ignore mixin method
      license_plate: this.checkoutForm_checkFieldVisibility('plate_number')
        ? this.userData.license_plate
        : '',
      // @ts-ignore mixin method
      zipcode: this.checkoutForm_checkFieldVisibility('zipcode')
        ? this.userData.user_zipcode
        : '',
      // @ts-ignore mixin method
      outbound_flight:
        this.checkoutForm_checkFieldVisibility('outbound_flight_number') ||
        this.isLoungesProduct
          ? this.userData.outbound_flight
          : '',
      // @ts-ignore mixin method
      inbound_flight: this.checkoutForm_checkFieldVisibility(
        'inbound_flight_number',
      )
        ? this.userData.inbound_flight
        : '',
      number_of_people_in_vehicle: this.checkoutForm_checkFieldVisibility(
        'number_of_people_in_vehicle',
      )
        ? this.userData.number_of_people_in_vehicle
        : null,
    };

    this.addVehicleFieldsToUserDetails();
    this.addTripDetailsToUserDetails();
  },
  async mounted() {
    const phoneNumberFieldVisible =
      this.checkoutForm_checkFieldVisibility('phone_number');
    const savedUserMarketingChannels =
      this.userMarketingSubscription?.marketing_channels;

    if (this.userDetails.marketing_channels) {
      if (this.isMarketingChannelAvailable('email')) {
        this.userDetails.marketing_channels.email =
          !!savedUserMarketingChannels?.email;
      }

      if (this.isMarketingChannelAvailable('sms')) {
        this.userDetails.marketing_channels.sms =
          !!savedUserMarketingChannels?.sms;
      }

      /**
       * Toggles the expansion state of the marketing pref dropdown
       * based on the availability of the phone number field and user preferences.
       */
      if (this.userDetails.marketing_channels) {
        const { sms, email } = this.userDetails.marketing_channels;
        this.showDropdownExpanded = phoneNumberFieldVisible
          ? !(sms && email)
          : !email;
      }

      this.showMarketingPreferences = true;
    }
    if (this.displayFlightLookup && this.searchCriteria) {
      if (this.checkoutForm_checkFieldVisibility('inbound_flight_number')) {
        await this.fetchFlightLookupDetails({
          ...this.getSearchCriteria('arrival'),
          direction: 'arrival',
        });
      }
      if (this.checkoutForm_checkFieldVisibility('outbound_flight_number')) {
        await this.fetchFlightLookupDetails({
          ...this.getSearchCriteria('departure'),
          direction: 'departure',
        });
      }

      this.prefillOutboundFlight();
      this.prefillInboundFlight();
    }
    if (
      !this.displayFlightLookup &&
      this.flightWasSelected &&
      !this.flightNumber
    ) {
      this.selectOutboundFlightLookup({ id: null });
    }

    if (this.displayLoungePassAdditionalTravellers) {
      this.setLoungePassAdditionalTravellersDetails();
    }
  },

  methods: {
    ...mapActions(useUserInfoStore, {
      fetchUserDetails: 'fetchUserDetails',
      fetchVehicleDetails: 'fetchVehicleDetails',
      fetchFlightLookupDetails: 'fetchFlightLookupDetails',
      storeInboundFlightLookup: 'selectInboundFlightLookup',
      storeOutboundFlightLookup: 'selectOutboundFlightLookup',
    }),
    ...mapActions(usePreloaderControllerStore, {
      commitUpdatePreloader: 'updatePreloader',
    }),
    /**
     * Returns true when the field should be visible.
     * In extend/upgrade flow (formLocked), fields that have no value are hidden — they
     * were added to the product after the original order was created and cannot be filled in.
     * Outside of extend flow, all fields are shown as normal.
     */
    isFieldVisibleInExtendFlow(value: unknown): boolean {
      return !this.formLocked || !!value;
    },

    onFormFieldBlur(field: string): void {
      // @ts-ignore - no types for user details validation object
      this.$v.userDetails[field].$touch();
    },
    isMarketingChannelAvailable(channel: string): boolean {
      return !!this.partnerMarketingPreferences?.marketing_channels?.includes(
        channel,
      );
    },

    /**
     * Handles form validation errors by scrolling to the first error field.
     */
    handleFormValidationErrors(): boolean {
      this.$v.$touch();

      if (
        this.$v.loungePassAdditionalTravellersDetails?.$invalid &&
        this.$refs.loungePassAdditionalTravellersComponent
      ) {
        this.$refs.loungePassAdditionalTravellersComponent.expandInvalidTravellers();
      }

      if (this.$v.$error) {
        // Scroll to the first field with an error
        smoothScroll('.input-container--error');
        return true;
      }

      return false;
    },

    validateForm() {
      // Validate form and stop if there are errors
      if (this.handleFormValidationErrors()) {
        return;
      }

      if (checkIsLessOneHour(this.entryDateTime, this.partnerTimeZone)) {
        const options = {
          path: '/',
          maxAge: 9999,
        } as CookieSerializeOptions;

        if (process.env.NODE_ENV !== 'production') {
          // if process run on 'localhost' we need to share token in sameSite
          options.sameSite = true;
        } else {
          // if process run on 'AWS' we need to share token in domain
          options.domain = getCookieDomain();
        }

        this.$cookies.set('crossAppError', 'lessOneHour', options);
        goToApp(
          getDomainUrl(DOMAINS_NAMES.ACCOUNT),
          ACCOUNT_URLS.MANAGE_BOOKING,
        );
        return;
      }

      if (!this.displayWindcaveGateway) {
        this.buttonLoading = true;
      }

      if (!this.paymentNeeded || this.isBookingPortalFlowEnabled) {
        // @ts-ignore - Mixin method
        this.checkoutForm_handlePaymentCompleted({
          payment_method_id: 'zero',
          merchant_id: 'zero',
        });
        this.buttonLoading = false;
      } else {
        // @ts-ignore
        this.$refs.paymentGateway.submitForm();
      }
    },
    /**
     * Validates the form and displays error messages without submitting.
     * Used by wallet payment methods (Apple Pay, Google Pay) to show validation
     * errors when the form is invalid before they attempt payment authorization.
     */
    showErrorIfFormInvalid(): void {
      this.handleFormValidationErrors();
    },
    /**
     * Displays payment decline error message and reload the widget for Windcave and Advam PSPs
     */
    errorHandler() {
      this.buttonLoading = false;
      this.$emit('error');
      this.handleWidgetReload();
    },
    /**
     * Displays technical error message and reload the widget for Windcave and Advam PSPs
     */
    showTechnicalError(): void {
      this.commitUpdatePreloader('');
      this.buttonLoading = false;
      this.$emit('show-technical-error');
      this.handleWidgetReload();
    },

    /**
     * Cancels the current transaction by resetting the loading state,
     * reloading the payment widget and cancelling booking creation
     * Currently used for Advam when Google Pay / Apple Pay is cancelled by the user
     */
    cancelTransaction(): void {
      this.commitUpdatePreloader('');
      this.buttonLoading = false;
      this.$emit('reloadPaymentWidget');
      this.$emit('cancel-booking');
    },
    /**
     * Handles the payment widget reload for Windcave and Advam PSPs.
     * These PSPs require calling /client-config to obtain a new session ID whenever
     * the price is updated (e.g., discount added/removed) or an error occurs,
     * as their widgets initialize only with a session ID.
     */
    handleWidgetReload(): void {
      if (
        this.displayWindcaveGateway ||
        this.displayAdvamGateway ||
        this.displaySycurioGateway()
      ) {
        this.$emit('reloadPaymentWidget');
      }
    },
    addVehicleFieldsToUserDetails() {
      this.userDetails.vehicle_make = this.checkoutForm_checkFieldVisibility(
        'vehicle_make',
      )
        ? this.userData.vehicle_make
        : '';
      this.userDetails.vehicle_model = this.checkoutForm_checkFieldVisibility(
        'vehicle_model',
      )
        ? this.userData.vehicle_model
        : '';
      this.userDetails.vehicle_colour = this.checkoutForm_checkFieldVisibility(
        'vehicle_colour',
      )
        ? this.userData.vehicle_colour
        : '';
    },
    addTripDetailsToUserDetails() {
      this.userDetails.terminal_name = this.checkoutForm_checkFieldVisibility(
        'terminal_name',
      )
        ? (this.userData.terminal_name ?? '')
        : '';
      this.userDetails.cruise_ship_name =
        this.checkoutForm_checkFieldVisibility('cruise_ship_name')
          ? (this.userData.cruise_ship_name ?? '')
          : '';
    },
    selectOutboundFlightLookup(flightLookup): void {
      this.userDetails.outbound_flight = flightLookup?.hasOwnProperty('id')
        ? flightLookup.id
        : '';
      this.userDetails.outbound_airline = flightLookup?.airlineName ?? '';
      this.userDetails.outbound_airport = flightLookup?.airport ?? '';
      this.userDetails.outbound_terminal = flightLookup?.terminalName ?? '';
      this.storeOutboundFlightLookup(flightLookup);
    },
    selectInboundFlightLookup(flightLookup): void {
      this.userDetails.inbound_flight = flightLookup?.hasOwnProperty('id')
        ? flightLookup.id
        : '';
      this.userDetails.inbound_airline = flightLookup?.airlineName ?? '';
      this.userDetails.inbound_airport = flightLookup?.airport ?? '';
      this.userDetails.inbound_terminal = flightLookup?.terminalName ?? '';
      this.storeInboundFlightLookup(flightLookup);
    },
    getSearchCriteria(direction: string): string {
      const date =
        direction === 'arrival'
          ? this.searchCriteria.date2
          : this.searchCriteria.date1;
      const time =
        direction === 'arrival'
          ? this.searchCriteria.time2
          : this.searchCriteria.time1;
      const airportCode = this.searchCriteria.airport_code;
      const dateTimeISO = `${date}T${time}:00.000Z`;
      const zonedDate = utcToZonedTime(new Date(dateTimeISO), 'UTC');
      return {
        datetime: format(zonedDate, DATE_TIME_FORMATS.year_month_day_utc),
        airportCode,
      };
    },
    prefillOutboundFlight(): void {
      if (this.flightWasSelected) {
        if (this.flightNumber) {
          const iata = this.searchCriteria?.airline_iata;
          const flightNumber = this.searchCriteria?.flight_number;
          const flightId =
            iata && flightNumber ? `${iata}-${flightNumber}` : null;
          const selectedOutboundFlight = flightId
            ? this.findOutboundFlightById(flightId)
            : { id: CANT_FIND_FLIGHT_ID };
          this.selectOutboundFlightLookup(selectedOutboundFlight);
        } else {
          this.selectOutboundFlightLookup({ id: CANT_FIND_FLIGHT_ID });
        }
      }
    },
    prefillInboundFlight(): void {
      if (this.userDetails.inbound_flight) {
        this.selectInboundFlightLookup(
          this.findInboundFlightById(this.userDetails.inbound_flight),
        );
      }
    },
    setLoungePassAdditionalTravellersDetails(): void {
      if (this.displayLoungePassAdditionalTravellers) {
        const maxTickets: number = getAppVariable(
          'products.lounges.customer_groups.max_tickets',
        );
        const groups = this.loungePassItem.search_criteria.lounges.groups;
        const travellers = Object.entries(groups).flatMap(([type, count]) => {
          let adjustedCount = type === 'adult' ? Math.max(0, count - 1) : count;
          const maxAdditionalTravellers = maxTickets - 1;

          if (adjustedCount > maxAdditionalTravellers) {
            adjustedCount = maxAdditionalTravellers;
          }

          type = type === 'senior' ? 'adult' : type;

          return Array.from({ length: adjustedCount }, () => ({
            firstName: '',
            lastName: '',
            ageGroup: type,
            ageGroupLabel: this.loungePassCustomerGroupLabels[type],
          }));
        });

        this.loungePassAdditionalTravellersDetails = travellers;
      } else {
        this.loungePassAdditionalTravellersDetails = [];
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    checkSuccess(
      validation: any,
      value: string | number | null,
      isRequired: boolean,
    ): boolean {
      if (isRequired) return validation.$anyDirty && !validation.$anyError;
      /**
       * For fields with only requiredIf=false validation (ex. zipcode) - validation.$anyError will always be false.
       * Extra check for the field value is required to prevent displaying the success state for an empty field.
       */
      return validation.$anyDirty && !validation.$anyError && !!value;
    },
    handlePlateNumberInput() {
      this.vehicleLookUp.error = false;
      this.vehicleLookUp.success = false;
      this.vehicleLookUp.vehicleMaxWeightConfirmed = false;
      this.vehicleLookUp.vehicleRoadworthyConfirmed = false;
    },
    changeVehicle() {
      this.vehicleLookUp.success = false;
      this.vehicleLookUp.showDetails = false;
      this.vehicleLookUp.vehicleMaxWeightConfirmed = false;
      this.vehicleLookUp.vehicleRoadworthyConfirmed = false;
      this.vehicleLookUp.vehicleRoadworthinessAndWeightError = '';
      this.$nextTick(() => {
        this.$refs.licensePlateTextField.$el.focus();
      });
    },

    async vehicleLookUpSubmit() {
      this.vehicleLookUp.error = false;
      this.vehicleLookUp.vehicleMaxWeightConfirmed = false;
      this.vehicleLookUp.grossVehicleWeight = null;
      this.vehicleLookUp.vehicleRoadworthyConfirmed = false;
      this.vehicleLookUp.vehicleRoadworthyValid = false;
      this.vehicleLookUp.vehicleRoadworthinessAndWeightError = '';
      // Validate license plate
      this.$v.userDetails.license_plate.$touch();
      if (this.$v.userDetails.license_plate.$error) return;
      this.vehicleLookUp.loading = true;
      this.vehicleLookUp.success = false;
      this.vehicleLookUp.enterManually = false;
      this.vehicleLookUp.showDetails = false;
      try {
        const params = [
          this.userDetails.license_plate,
          this.isVehicleRoadworthinessAndWeightEnabled &&
            this.checkoutForm_checkFieldVisibility('vehicle_roadworthy'),
          this.isVehicleRoadworthinessAndWeightEnabled &&
            this.checkoutForm_checkFieldVisibility('max_vehicle_weight'),
        ];
        const details = await this.fetchVehicleDetails(...params);
        this.userDetails.vehicle_make = details.make;
        this.userDetails.vehicle_model = details.model;
        this.userDetails.vehicle_colour = details.colour;

        if (this.isVehicleRoadworthinessAndWeightValidationEnabled) {
          this.vehicleLookUp.grossVehicleWeight = details.grossVehicleWeight;
          this.vehicleLookUp.vehicleRoadworthyValid =
            details.motStatus === 'Y' || details.motStatus === 'X';

          if (
            this.checkoutForm_checkFieldVisibility('max_vehicle_weight') &&
            details.grossVehicleWeight === null
          ) {
            datadogRum.addError('Vehicle validation error: car weight is null');
          }

          if (
            this.checkoutForm_checkFieldVisibility('max_vehicle_weight') &&
            details.grossVehicleWeight > 2500
          ) {
            datadogRum.addError(
              'Vehicle validation error: Vehicle exceeds allowed weight',
            );
          }

          if (
            this.checkoutForm_checkFieldVisibility('vehicle_roadworthy') &&
            details.motStatus !== 'Y' &&
            details.motStatus !== 'X'
          ) {
            datadogRum.addError(
              'Vehicle validation error: Vehicle did not have a valid MOT',
            );
          }
        }

        this.vehicleLookUp.showDetails = true;
        this.vehicleLookUp.success = true;
        this.vehicleLookUp.enterManually = false;

        addCustomMetric(
          'tracking',
          'Vehicle Lookup: Success',
          {
            partner_id: this.partnerId,
          },
          null,
          'vehicle_lookup',
        );
      } catch (error) {
        addCustomMetric(
          'error',
          'Vehicle Lookup: Error',
          {
            partner_id: this.partnerId,
            error: error instanceof Error ? error.message : String(error),
          },
          null,
          'vehicle_lookup',
        );
        this.vehicleLookUp.showDetails = false;

        if (
          this.isVehicleRoadworthinessAndWeightValidationEnabled &&
          (this.checkoutForm_checkFieldVisibility('vehicle_roadworthy') ||
            this.checkoutForm_checkFieldVisibility('max_vehicle_weight'))
        ) {
          this.vehicleLookUp.vehicleRoadworthinessAndWeightError =
            'Something went wrong, please try again';

          datadogRum.addError('Vehicle validation error: API request failed');
        } else {
          this.vehicleLookUp.error = true;
          this.vehicleLookUp.enterManually = true;
        }
      } finally {
        this.vehicleLookUp.loading = false;
      }
    },

    keydownSpaceHandler(e: KeyboardEvent) {
      if (e.target instanceof HTMLElement) {
        e.target.click();
      }
    },

    vehicleMaxWeightClickHandler(
      value: boolean,
      event: HTMLInputElement,
    ): void {
      if (
        this.displayVehicleLookupButton &&
        !this.vehicleMaxWeightValid &&
        this.isVehicleRoadworthinessAndWeightValidationEnabled &&
        this.checkoutForm_checkFieldVisibility('plate_number')
      ) {
        if (event?.target instanceof HTMLInputElement) {
          event.target.checked = false;
        }

        this.vehicleLookUp.vehicleRoadworthinessAndWeightError =
          'Weight validation failed';

        const header =
          this.vehicleLookUp.grossVehicleWeight === null
            ? "Sorry, we were unable to validate the vehicle's weight."
            : 'Sorry, the vehicle is over the allowance for this car park.';
        const btnText =
          this.vehicleLookUp.grossVehicleWeight === null
            ? 'Select different product'
            : 'Search again';

        this.$openModal('GlobalModalError', {
          header,
          body: 'Please try searching again and select a different option.',
          buttons: [
            {
              callback: this.changeVehicle,
              btnText: 'Change licence plate',
            },
            {
              callback: this.searchAgain,
              btnText,
            },
          ],
        })
          .then((callback) => {
            if (callback && typeof callback === 'function') {
              callback();
            }
          })
          .catch(() => {});
      } else {
        this.vehicleLookUp.vehicleMaxWeightConfirmed = value;
      }
    },

    vehicleRoadworthyClickHandler(
      value: boolean,
      event: HTMLInputElement,
    ): void {
      if (
        this.displayVehicleLookupButton &&
        !this.vehicleLookUp.vehicleRoadworthyValid &&
        this.isVehicleRoadworthinessAndWeightValidationEnabled &&
        this.checkoutForm_checkFieldVisibility('plate_number')
      ) {
        if (event?.target instanceof HTMLInputElement) {
          event.target.checked = false;
        }
        this.vehicleLookUp.vehicleRoadworthinessAndWeightError =
          'MOT validation failed';

        this.$openModal('GlobalModalError', {
          header:
            'Sorry, the MOT for this vehicle shows expiry before the car park exit date.',
          body: 'Please try a different vehicle or update your MOT before booking.',
          buttons: [
            {
              callback: this.changeVehicle,
              btnText: 'Change licence plate',
            },
            {
              callback: this.searchAgain,
              btnText: 'Search again',
            },
          ],
        })
          .then((callback) => {
            if (callback && typeof callback === 'function') {
              callback();
            }
          })
          .catch(() => {});
      } else {
        this.vehicleLookUp.vehicleRoadworthyConfirmed = value;
      }
    },

    displaySycurioGateway(): boolean {
      return isSycurioFlow(this.$route);
    },
  },
});
